import { COOKIE_CONSENT_CHANGED_EVENT, getConsent } from './cookie-consent';
import { ROUTE_CHANGE_EVENT } from '../router';

declare global {
  interface Window {
    // gtag.js expects Arguments objects in dataLayer (official snippet).
    dataLayer?: Array<IArguments | Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID || 'AW-18068383353';

const CONVERSION_SEND_TO: Record<AdsConversion, string | undefined> = {
  phone: import.meta.env.VITE_ADS_CONVERSION_PHONE,
  calendar: import.meta.env.VITE_ADS_CONVERSION_CALENDAR,
  form: import.meta.env.VITE_ADS_CONVERSION_FORM,
  email: import.meta.env.VITE_ADS_CONVERSION_EMAIL,
};

const GA_EVENT: Record<AdsConversion, string> = {
  phone: 'phone_call',
  calendar: 'book_appointment',
  form: 'generate_lead',
  email: 'email_click',
};

export type AdsConversion = 'phone' | 'calendar' | 'form' | 'email';

const CONSENT_DENIED = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
} as const;

const CONSENT_GRANTED = {
  analytics_storage: 'granted',
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
} as const;

let booted = false;
let lastTrackedPath: string | null = null;

function ensureGtag(): void {
  window.dataLayer = window.dataLayer ?? [];
  if (window.gtag) return;
  // Official gtag snippet pushes the Arguments object, not a rest-parameter array.
  // Pushing an Array can leave config commands unprocessed → no hits in Realtime.
  window.gtag = function gtag(..._args: unknown[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
}

function applyConsent(granted: boolean): void {
  if (!window.gtag) return;
  window.gtag('consent', 'update', granted ? CONSENT_GRANTED : CONSENT_DENIED);
  window.gtag('set', 'ads_data_redaction', !granted);
}

function bootTags(): void {
  if (booted || (!GA_ID && !ADS_ID)) return;
  booted = true;

  ensureGtag();
  window.gtag!('consent', 'default', {
    ...CONSENT_DENIED,
    wait_for_update: 500,
  });
  window.gtag!('set', 'url_passthrough', true);
  window.gtag!('set', 'ads_data_redaction', true);
  applyConsent(getConsent()?.analytics === true);

  window.gtag!('js', new Date());
  if (GA_ID) {
    window.gtag!('config', GA_ID, {
      anonymize_ip: true,
      send_page_view: false,
    });
  }
  if (ADS_ID) {
    window.gtag!('config', ADS_ID);
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID || ADS_ID}`;
  document.head.appendChild(script);

  trackPageView();
}

export function trackPageView(pathname = location.pathname): void {
  if (!booted || !GA_ID || !window.gtag) return;
  if (pathname === lastTrackedPath) return;
  lastTrackedPath = pathname;

  window.gtag('event', 'page_view', {
    page_path: pathname,
    page_location: location.href,
    page_title: document.title,
  });
}

function conversionSendTo(kind: AdsConversion): string | undefined {
  const label = CONVERSION_SEND_TO[kind]?.trim();
  if (!label) return undefined;
  if (label.includes('/')) return label;
  if (!ADS_ID) return undefined;
  return `${ADS_ID}/${label}`;
}

/** Fires even without cookie consent so Consent Mode can model the conversion. */
export function trackConversion(kind: AdsConversion): void {
  if (!booted || !window.gtag) return;

  window.gtag('event', GA_EVENT[kind], {
    method: kind,
    page_path: location.pathname,
  });

  const sendTo = conversionSendTo(kind);
  if (!sendTo) return;
  window.gtag('event', 'conversion', { send_to: sendTo });
}

export function initAnalytics(): void {
  bootTags();
  if (booted) applyConsent(getConsent()?.analytics === true);
}

export function setupAnalyticsListener(): void {
  window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, () => {
    initAnalytics();
  });

  window.addEventListener(ROUTE_CHANGE_EVENT, ((event: CustomEvent<{ pathname: string }>) => {
    trackPageView(event.detail.pathname);
  }) as EventListener);
}
